import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { LiveDriver } from '../api/endpoints';

/**
 * Подписка кабинета на живые обновления (`/ws/live`, FR-573).
 *
 * **Сокет — транспорт, а не источник истины** (разд. 7 брифа). Экран продолжает
 * опрашивать REST и без сокета работает полностью: подписка только сокращает задержку
 * между координатой и картой. Поэтому здесь нет ни загрузки состояния, ни обработки
 * ошибок «не удалось подключиться» — потеря сокета не ошибка экрана, а его штатный
 * режим, о котором сказано значком состояния.
 *
 * Позиции складываются по водителю, а не в список: сервер шлёт обновления пачками по
 * тем, кто прислал координату, и склейка по идентификатору — единственный способ не
 * терять остальных.
 */

export type LiveState = 'connected' | 'connecting' | 'offline';

export interface LiveSocket {
  readonly state: LiveState;
  /** Позиции, пришедшие сокетом после последнего опроса. */
  readonly positions: ReadonlyMap<string, LiveDriver>;
  /** Версии заданий: сигнал перечитать задание по REST, а не само задание. */
  readonly taskRevisions: ReadonlyMap<string, number>;
}

export function useLiveSocket(enabled = true): LiveSocket {
  const [state, setState] = useState<LiveState>('offline');
  const [positions, setPositions] = useState<ReadonlyMap<string, LiveDriver>>(new Map());
  const [taskRevisions, setTaskRevisions] = useState<ReadonlyMap<string, number>>(new Map());
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    setState('connecting');

    /*
     * Кабинет и API на одном домене (nginx проксирует `/ws/`), поэтому адрес —
     * относительный: захардкоженный хост сломал бы вторую инсталляцию, которая
     * живёт на другом домене из того же образа (FR-411, A15).
     */
    const socket = io('/ws/live', {
      path: '/socket.io',
      transports: ['websocket'],
      withCredentials: true,
    });

    socketRef.current = socket;

    socket.on('connect', () => setState('connected'));
    socket.on('disconnect', () => setState('offline'));
    socket.io.on('reconnect_attempt', () => setState('connecting'));

    socket.on('positions', (payload: { positions?: LiveDriver[] }) => {
      const incoming = payload.positions ?? [];

      setPositions((current) => {
        const next = new Map(current);

        for (const position of incoming) {
          if (position.driver_id !== undefined && position.driver_id !== null) {
            next.set(position.driver_id, position);
          }
        }

        return next;
      });
    });

    socket.on('task.changed', (payload: { task_id?: string; revision?: number }) => {
      if (payload.task_id === undefined || payload.revision === undefined) {
        return;
      }

      setTaskRevisions((current) => new Map(current).set(payload.task_id!, payload.revision!));
    });

    return () => {
      socket.close();
      socketRef.current = null;
      setState('offline');
    };
  }, [enabled]);

  return { state, positions, taskRevisions };
}

/**
 * Склейка снапшота REST с тем, что пришло сокетом.
 *
 * Побеждает более свежая координата, а не сокет: снапшот приходит реже, но может
 * оказаться новее — например, сразу после переподключения.
 */
export function mergePositions(
  snapshot: readonly LiveDriver[],
  live: ReadonlyMap<string, LiveDriver>,
): LiveDriver[] {
  return snapshot.map((driver) => {
    const fresh = driver.driver_id === undefined ? undefined : live.get(driver.driver_id);

    if (fresh === undefined) {
      return driver;
    }

    const fromSocket = new Date(fresh.recorded_at ?? 0).getTime();
    const fromSnapshot = new Date(driver.recorded_at ?? 0).getTime();

    return fromSocket > fromSnapshot ? fresh : driver;
  });
}
